import React, { useState, useEffect } from 'react';
import { Tag, Plus, Search, Pencil, Trash2, Loader2, X, Check, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, AlertTriangle, RefreshCw } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
import { hasPermission } from '../../utils/permissionsHelper';
import { useClientLevel } from '../../hooks/useClientLevel';


interface TagData {
    id: number;
    name: string;
    created?: string;
    organization_id?: number;
    client_id?: number | null;
}

interface DeleteConfirmationModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    tagName: string | undefined;
    isDeleting: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({ open, onClose, onConfirm, tagName, isDeleting }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-3">
            <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl max-w-xs w-full overflow-hidden transform transition-all animate-in zoom-in duration-200">
                <div className="p-4">
                    <div className="flex justify-between items-start mb-3">
                        <div className="p-2 bg-red-500/20 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                        </div>
                        <button onClick={onClose} disabled={isDeleting} className="text-gray-100 hover:text-white transition-colors disabled:opacity-50">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <h3 className="text-sm font-bold text-white mb-1">Delete Tag?</h3>
                    <p className="text-gray-100 text-[11px] leading-relaxed mb-5">
                        Are you sure you want to delete <span className="text-white font-bold">"{tagName}"</span>? This action cannot be undone.
                    </p>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            disabled={isDeleting}
                            className="flex-1 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-100 rounded-lg text-[11px] font-bold transition-colors disabled:opacity-50 uppercase tracking-wider"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onConfirm}
                            disabled={isDeleting}
                            className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-[11px] font-bold transition-colors flex items-center justify-center disabled:opacity-50 uppercase tracking-wider"
                        >
                            {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Delete'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const TagsSettings: React.FC = () => {
    const [tags, setTags] = useState<TagData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [newTagName, setNewTagName] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [editingTag, setEditingTag] = useState<TagData | null>(null);
    const [editTagName, setEditTagName] = useState('');
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [isDeleting, setIsDeleting] = useState<number | null>(null);
    const level = useClientLevel();

    // Delete Modal State
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [tagToDelete, setTagToDelete] = useState<TagData | null>(null);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(30);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const canCreate = hasPermission('tags', 'create')
    const canUpdate = hasPermission('tags', 'update')
    const canDelete = hasPermission('tags', 'delete')

    const fetchTags = async (skip: number = 0, limit: number = itemsPerPage, query: string = searchTerm) => {
        setIsLoading(true);
        try {
            const queryObj: any = {};
            if (level.name !== 'global') {
                queryObj.client_id = level.id;
            }
            if (query.trim()) {
                queryObj.name = { "$ilike": `%${query}%` };
            }


            const params = {
                query: JSON.stringify(queryObj),
                skip,
                limit
            };

            const response = await baseapi.get('/tags', { params });
            if (response.data.status) {
                setTags(response.data.data);
                setTotalItems(response.data.total);
                setTotalPages(Math.ceil(response.data.total / limit));
            }
        } catch (error: any) {
            console.error('Failed to fetch tags', error);
            showError('Failed to load tags');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const skip = (currentPage - 1) * itemsPerPage;

        fetchTags(skip, itemsPerPage, searchTerm);

    }, [searchTerm, currentPage, itemsPerPage, level.id]);

    const handleAddTag = async () => {
        if (!newTagName.trim()) return;
        const params: any = {
            name: newTagName.trim(),
            ...(level?.id && { client_id: level.id }),
        }
        setIsAdding(true);
        try {
            const response = await baseapi.post('/tags', params);
            if (response.data.status) {
                showSuccess('Tag created successfully');
                setNewTagName('');
                setCurrentPage(1); // Go to first page to see new tag if sorted by new
                fetchTags(0, itemsPerPage, searchTerm);
            }
        } catch (error: any) {
            showError(error.message || 'Failed to create tag');
        } finally {
            setIsAdding(false);
        }
    };

    const handleEditTag = async () => {
        if (!editingTag || !editTagName.trim()) return;
        setIsSavingEdit(true);
        try {
            const response = await baseapi.put(`/tags/${editingTag.id}`, { name: editTagName.trim() });
            if (response.data.status) {
                showSuccess('Tag updated successfully');
                setEditingTag(null);
                const skip = (currentPage - 1) * itemsPerPage;
                fetchTags(skip, itemsPerPage, searchTerm);
            }
        } catch (error: any) {
            showError(error.message || 'Failed to update tag');
        } finally {
            setIsSavingEdit(false);
        }
    };

    const handleDeleteClick = (tag: TagData) => {
        setTagToDelete(tag);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!tagToDelete) return;
        setIsDeleting(tagToDelete.id);
        try {
            const response = await baseapi.delete(`/tags/${tagToDelete.id}`);
            if (response.data.status) {
                showSuccess('Tag deleted successfully');
                const skip = (currentPage - 1) * itemsPerPage;
                fetchTags(skip, itemsPerPage, searchTerm);
            }
        } catch (error: any) {
            showError(error.message || 'Failed to delete tag');
        } finally {
            setIsDeleting(null);
            setDeleteModalOpen(false);
            setTagToDelete(null);
        }
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(1);
    };

    const startEditing = (tag: TagData) => {
        setEditingTag(tag);
        setEditTagName(tag.name);
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Date unknown';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    return (
        <div className="space-y-4 pb-20">
            {/* Main Header Card with Add and Search */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-2 shadow-xl">
                <div className="flex flex-col md:flex-row items-center justify-between gap-2">
                    {/* Left side: Add Tag field */}
                    {canCreate && <div className="flex items-center gap-3 w-full md:w-auto">
                        <div className="relative flex-1 md:w-64">
                            <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-100" />
                            <input
                                type="text"
                                placeholder="Enter tag name..."
                                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-sm text-white focus:border-orange-500 outline-none transition-all placeholder:text-gray-100"
                                value={newTagName}
                                onChange={(e) => {
                                    setNewTagName(e.target.value);
                                }}
                                onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                            />
                        </div>
                        <button
                            onClick={handleAddTag}
                            disabled={isAdding || !newTagName.trim()}
                            className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all shadow-lg shadow-orange-900/10"
                        >
                            {isAdding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            Add
                        </button>
                    </div>}

                    {/* Right side: Search field & Refresh */}
                    <div className="flex items-center gap-2 w-full md:w-auto justify-end">
                        <button
                            onClick={() => {
                                const skip = (currentPage - 1) * itemsPerPage;
                                fetchTags(skip, itemsPerPage, searchTerm);
                            }}
                            className="rounded-lg bg-gray-800 border border-gray-700 text-gray-200 p-2 hover:bg-gray-700 transition-colors shrink-0"
                            title="Refresh"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
                        </button>
                        <div className="relative w-full sm:w-56">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search tags..."
                                className="w-full bg-gray-800/50 border border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-white focus:border-orange-500 outline-none transition-all"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Tag Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                {isLoading && tags.length === 0 ? (
                    <div className="col-span-full py-20 flex flex-col items-center justify-center text-gray-100 space-y-4">
                        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                        <p className="text-sm font-medium uppercase tracking-widest">Loading tags...</p>
                    </div>
                ) : tags.length === 0 ? (
                    <div className="col-span-full py-20 flex flex-col items-center justify-center text-gray-100 space-y-4 bg-gray-900/30 border border-dashed border-gray-800 rounded-xl">
                        <Tag className="w-12 h-12 text-gray-100" />
                        <p className="text-sm font-medium">No tags found. Create your first tag above.</p>
                    </div>
                ) : (
                    tags.map((tag) => (
                        <div key={tag.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4 hover:border-orange-500/30 transition-all group relative">
                            {editingTag?.id === tag.id ? (
                                <div className="space-y-3">
                                    <input
                                        type="text"
                                        autoFocus
                                        className="w-full bg-gray-800 border border-orange-500/50 rounded-lg px-3 py-1.5 text-sm text-white outline-none"
                                        value={editTagName}
                                        onChange={(e) => setEditTagName(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleEditTag()}
                                    />
                                    <div className="flex justify-end gap-2">
                                        <button onClick={() => setEditingTag(null)} className="p-1 text-gray-100 hover:text-white transition-colors">
                                            <X className="w-4 h-4" />
                                        </button>
                                        <button onClick={handleEditTag} disabled={isSavingEdit} className="p-1 text-green-500 hover:text-green-400 transition-colors">
                                            {isSavingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <div className="bg-orange-600/10 p-1.5 rounded-lg border border-orange-600/20">
                                                <Tag className="w-3.5 h-3.5 text-orange-500" />
                                            </div>
                                            <span className="text-sm font-bold text-gray-100 pr-2 max-w-[120px]" title={tag.name}>
                                                {tag.name}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {canUpdate && <button
                                                onClick={() => startEditing(tag)}
                                                className="p-1.5 text-gray-100 hover:text-blue-400 hover:bg-blue-400/10 rounded-lg transition-all"
                                                title="Edit tag"
                                            >
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>}
                                            {canDelete && <button
                                                onClick={() => handleDeleteClick(tag)}
                                                disabled={isDeleting === tag.id}
                                                className="p-1.5 text-gray-100 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all"
                                                title="Delete tag"
                                            >
                                                {isDeleting === tag.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                            </button>}
                                        </div>
                                    </div>
                                    <div className="pt-3 border-t border-gray-800/50 flex flex-col gap-1">
                                        <span className="text-[10px] text-gray-100 font-bold uppercase tracking-wider">Created</span>
                                        <span className="text-[11px] text-gray-100 font-medium">
                                            {formatDate(`${tag.created}z`)}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>
                    ))
                )}
            </div>

            {/* Pagination UI */}
            {!isLoading && tags.length > 0 && (
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl px-4 py-3 flex items-center justify-between mt-auto">
                    <div className="flex items-center gap-4">
                        <ItemsPerPageDropdown value={itemsPerPage} onChange={handleItemsPerPageChange} />
                        <span className="text-[10px] text-gray-100 font-bold uppercase tracking-widest">
                            <span className="text-gray-100">{startIndex + 1}</span> - <span className="text-gray-100">{Math.min(endIndex, totalItems)}</span> OF {totalItems}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                            className="p-1.5 text-gray-100 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronsLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="p-1.5 text-gray-100 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">
                            {currentPage}
                        </div>
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="p-1.5 text-gray-100 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                            className="p-1.5 text-gray-100 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                            <ChevronsRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            <DeleteConfirmationModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                tagName={tagToDelete?.name}
                isDeleting={isDeleting !== null}
            />
        </div>
    );
};

export default TagsSettings;
